import { createAsyncThunk, createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { CriteriaAndPaging, fetchAll } from "./OrderAPI";
import { RootState } from "../../app/store";
import { SliceState } from "../../utils/Utils";
import { OrderSearchCriteria } from "../admin/order/OrderSearchCriteria";
import {apiSlice} from "../../app/apiSlice"; // Import the RTK Query slice
import {OrderCreationResponse, OrderVm} from "./Order";
import {OrderStatus} from "../order-status/OrderStatus";
import {addDays, formatDateForInput, today} from "../../utils/dates";
import {defaultStorage} from "../../persistence";
import {SortState} from "../../utils/Grid";

export const STORAGE_KEY_ORDER_SEARCH_CRITERIA = 'order-search-criteria';
const initialCriteriaJson = defaultStorage.getItem(STORAGE_KEY_ORDER_SEARCH_CRITERIA);
const initialCriteria = initialCriteriaJson 
    ? JSON.parse(initialCriteriaJson) as OrderSearchCriteria 
    : {
        startDate: formatDateForInput(addDays(-7, today())),
        endDate: formatDateForInput(addDays(0, today())),
    };

// Define the state interface
export interface OrderState {
    orders?: OrderVm[];
    status: SliceState;
    canGetMore: boolean;
    criteria: OrderSearchCriteria;
    latestConsumerOrderResponse: OrderCreationResponse | null;
    orderStatuses: OrderStatus[]; // List of order statuses
    orderStatusesStatus: SliceState; // State to track loading status
    sort: SortState<OrderVm>;
}

// Initial state
const initialState: OrderState = {
    orders: undefined,
    status: 'idle',
    canGetMore: true,
    criteria: {},
    latestConsumerOrderResponse: null,
    orderStatuses: [], // Initial empty array for statuses
    orderStatusesStatus: 'idle', // Default is idle
    sort: {
        column: 'timeCreated',
        serverColumn: 'o.TimeCreated',
        direction: 'desc',
    }
};

// Async thunk for fetching orders
export const fetchOrders = createAsyncThunk(
    'orders/fetchAll',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

// Async thunk for fetching more orders
export const fetchMoreOrders = createAsyncThunk(
    'orders/fetchMore',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

export const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        changeSearchCriteria: (state) => {
            state.status = 'idle';
        },
        setOrderSearchCriteria: (state, { payload }: PayloadAction<OrderSearchCriteria>) => {
            state.criteria = payload;
        },
        setLatestOrderCreationResponse: (state, { payload: orderCreationResponse }: PayloadAction<OrderCreationResponse>) => {
            state.latestConsumerOrderResponse = orderCreationResponse;
        },
        setOrderSearchSort: (state, {payload}: PayloadAction<SortState<OrderVm>>) => {
            state.sort = payload;
        }
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchOrders
            .addCase(fetchOrders.pending, (state) => {
                state.status = 'loading';
                state.orders = [];
            })
            .addCase(fetchOrders.fulfilled, (state, { payload }) => {
                state.status = 'succeeded';
                state.orders = payload;
                state.canGetMore = payload.length === 50;
            })
            .addCase(fetchOrders.rejected, (state) => {
                state.status = 'failed';
                state.canGetMore = false;
            })

            // Handle fetchMoreOrders
            .addCase(fetchMoreOrders.pending, (state, { payload }) => {
                state.status = 'loading'
            })
            .addCase(fetchMoreOrders.fulfilled, (state, { payload }) => {
                state.status = 'succeeded';
                if (payload.length) state.orders = state.orders?.concat(payload);
                state.canGetMore = payload.length === 50;
            })

            // Handle getOrderStatuses endpoint using matchers
            .addMatcher(
                apiSlice.endpoints.getOrderStatuses.matchPending,
                (state) => {
                    state.orderStatusesStatus = 'loading'; // Set to loading
                }
            )
            .addMatcher(
                apiSlice.endpoints.getOrderStatuses.matchFulfilled,
                (state, { payload }) => {
                    state.orderStatuses = payload; // Populate order statuses
                    state.orderStatusesStatus = 'succeeded'; // Set to succeeded
                    if (!state.criteria.statusIds) {
                        state.criteria.statusIds = payload.map(s => s.id)
                    }
                }
            )
            .addMatcher(
                apiSlice.endpoints.getOrderStatuses.matchRejected,
                (state) => {
                    state.orderStatusesStatus = 'failed'; // Set to failed in case of error
                }
            );
    },
});

// Selectors
export const selectOrders = (state: RootState) => state.orders.orders;
export const selectOrdersStatus = (state: RootState) => state.orders.status;
export const selectOrderStatusesStatus = (state: RootState) => state.orders.orderStatusesStatus;
export const canFetchMoreOrders = (state: RootState) => state.orders.canGetMore;
export const selectOrderSearchCriteria = (state: RootState) => state.orders.criteria;
export const selectLatestConsumerOrderId = (state: RootState) => state.orders.latestConsumerOrderResponse;

// Memoized selector to check if fetching is in progress
export const isFetchingOrders = createSelector(
    [selectOrdersStatus, selectOrderStatusesStatus],
    (ordersStatus, orderStatusesStatus) =>
        ordersStatus === 'loading' || // If orders are loading
        // ordersStatus === 'idle' ||   // If orders are idle
        orderStatusesStatus === 'loading' // If statuses are loading
);


// Selector for order statuses using createSelector
export const selectOrderStatuses = createSelector(
    [(state: RootState) => state.orders.orderStatuses],
    (orderStatuses) => orderStatuses
);

export const selectOrderSearchSortState = createSelector(
    [(state: RootState) => state.orders.sort],
    sort => sort
)

// Export actions and the reducer
const { actions, reducer } = orderSlice;

export const {
    changeSearchCriteria,
    setOrderSearchCriteria,
    setLatestOrderCreationResponse,
    setOrderSearchSort,
} = actions;

export default reducer;